import React, { useState, useEffect, useMemo, useRef } from "react";
import queryString from "query-string";
import { useLocation, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { PageHeaderV2 } from "components/PageHeader";
import { WithTransparentSpinner } from "components/Hoc/WithTransparentSpinner";
import { CheckoutPaymentMethodN } from "components/checkout";
import * as CommonUI from "components";
import useDineInSuccesOrder from "modules/dine-in/hooks/useSuccesOrder";
import useSuccesOrder from "features/hooks/useSuccesOrder";
import usePlaceOrderHelper from "features/payment-helpers/usePlaceOrderHelper";

import { getPaymentLinkRedirectionObj } from "utils/Utils";
import { PaymentMethodAction } from "store/actions";
import C from "Constants";
import AFS_API from "./helpers/api";

const AfsPaymentConfirmation = (
  { paymentLinkInfo = {} } = {},
  fetchPaymentLinkInfo = () => {},
) => {
  const location = useLocation();
  const params = useParams();

  const queryParams = queryString.parse(location.search);

  const dispatch = useDispatch();

  const { selectedPaymentType } = useSelector(
    (state) => state.PaymentMethodReducer,
  );
  const { brandInfo } = useSelector((state) => state?.brandStorageReducer);
  const { selectedStore } = useSelector((state) => state.store);
  const { orderType, DomainType } = useSelector((state) => state.AppReducer);
  const { checkoutLoader } = useSelector((state) => state.loader);
  const { error, errorModaL: reduxErrorModal } = useSelector(
    (rstate) => rstate.errorSlice,
  );

  const isDineIn =
    orderType?.serviceType === C.SERVICE_TYPE.DINE_IN ||
    window?.location?.pathname?.includes("/dine-in");

  const handleDineInSuccessOrderRoute = useDineInSuccesOrder();
  const handleSuccessOrderRoute = useSuccesOrder();

  const { outlet_based_place_order, brand_based_place_order } =
    usePlaceOrderHelper(
      isDineIn ? handleDineInSuccessOrderRoute : handleSuccessOrderRoute,
    );

  const [isLoading, setIsLoading] = useState(true);
  const [errorModal, setErrorModal] = useState({
    open: false,
    error: null,
  });
  const isMountedRef = useRef(true);

  const paymentLinkRedirectionObj = useMemo(() => {
    if (!paymentLinkInfo?.amount) {
      return "/";
    }
    const obj = {
      token: null,
      cartId: paymentLinkInfo?.cartId,
      brandId: paymentLinkInfo?.brandId || selectedStore?.brandId,
      DomainType: DomainType,
      domainName: params?.domainName,
    };
    return getPaymentLinkRedirectionObj(obj);
  }, [paymentLinkInfo]);

  const handleError = () => {
    if (paymentLinkInfo?.amount) {
      const path = paymentLinkRedirectionObj.failureUrl.replace(
        "paymentStatus=failure",
        "afsPaymentError=afs-error",
      );
      window.location.replace(path);
    } else {
      window.history.go(-1);
    }
  };

  const preparePaymentIntent = async (rawResponse) => {
    let paymentIntent = {};
    paymentIntent.card = rawResponse?.card;
    paymentIntent.ipAddress = rawResponse?.customer?.ip;
    paymentIntent.paymentId = rawResponse?.id;
    paymentIntent.checkoutId = queryParams.checkoutId;
    paymentIntent.ndc = rawResponse?.ndc;
    paymentIntent.brand = rawResponse?.paymentBrand;
    paymentIntent.paymentProvider = C.AFS_PAYMENT;
    paymentIntent.amount = rawResponse?.amount;
    paymentIntent.currency = rawResponse?.currency;
    paymentIntent.isAfsDev = process.env.REACT_APP_AFS_ENV === "aslase-dev";

    if (rawResponse?.paymentCheckoutIntent) {
      const { result, integrity, orderMetaData, ...rest } = rawResponse?.paymentCheckoutIntent;
      paymentIntent.paymentCheckoutIntent = rest;
    }
    return paymentIntent;
  };

  const handlePlaceOrder = async (response) => {
    try {
      if (response?.data?.isOrderAlreadyPlaced) {
        isDineIn
          ? handleDineInSuccessOrderRoute()
          : handleSuccessOrderRoute();
        return;
      } else {
        const paymentIntent = await preparePaymentIntent(response?.data);
        outlet_based_place_order({ paymentIntent });
      }
    } catch (error) {
     throw error;
    }
   
  }

  const handleUpdatePaymentLinkInfo = async (response) => {
    try {
      await AFS_API.updatePaymentLinkInfo({
        cartId: paymentLinkInfo?.cartId,
        brandId: paymentLinkInfo?.brandId,
        paymentStatus: C.PAYMENT_LINKS.VERIFIED,
        paymentGateway: C.AFS_PAYMENT,
        linkStatus: C.PAYMENT_LINKS.PAID,
        transactionDetail: {
          id: response?.data?.id,
          amount:response?.data?.amount,
          currency: response?.data?.currency,
          paymentType: response?.data?.paymentType,
          paymentBrand: response?.data?.paymentBrand,
          merchantTransactionId: response?.data?.merchantTransactionId,
          card: response?.data?.card,
          ipAddress: response?.data?.customer?.ip,
          ndc: response?.data?.ndc,
          timestamp: response?.data?.timestamp,
          paymentId: response?.data?.id,
          checkoutId: queryParams.checkoutId,
        },
      });
  
      window.location.replace(paymentLinkRedirectionObj.successUrl);
    } catch (error) {
      throw error;
    }
   
  }

  const checkAFSPaymentStatus = async () => {
    
    if (!isMountedRef.current) return;

    try {
      const payload = {
        cartId: paymentLinkInfo?.cartId,
        storeId: selectedStore?._id,
        brandId: brandInfo?.brand?._id || selectedStore?.brandId,
        checkoutId: queryParams.checkoutId,
        isPaymentLink: paymentLinkInfo?.amount ? true : false,
      };

      const signedPayload = await btoa(JSON.stringify(payload));
      const response = await AFS_API.checkAFSPaymentStatus({
        signedUrl: signedPayload,
      });

      if (!selectedPaymentType) {
        dispatch(PaymentMethodAction.setSelectedPaymentType(C.AFS_PAYMENT));
      }

      // if payment link is not present, then check if the order is already placed or not by afs payment
      if (!paymentLinkInfo?.amount) {
        await handlePlaceOrder(response);
      } 
      // if payment link is present, then update the payment link info
      else if (paymentLinkInfo?.amount) { 
        await handleUpdatePaymentLinkInfo(response);
      }


    } catch (error) {
      if (isMountedRef.current) {
        setErrorModal({
          open: true,
          error:
            error?.message || "Something went wrong. Please try again later.",
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
        sessionStorage.removeItem("AFS_PAYMENT");
      }
    }
  };

  useEffect(() => {
    localStorage.removeItem("activePaymentMethod");
    isMountedRef.current = true;
    if (queryParams?.checkoutId) {
      checkAFSPaymentStatus();
      // window.history.back();
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [queryParams.checkoutId]);

  useEffect(() => {
    if (reduxErrorModal) {
      setErrorModal({
        open: reduxErrorModal,
        error: error,
      });
    }
  }, [reduxErrorModal]);

  return (
    <>
      <WithTransparentSpinner loading={isLoading || checkoutLoader} />
      <div className="checkout-page min-h-app-height store-page-popup">
        {paymentLinkInfo?.amount ? null : (
          <>
            <PageHeaderV2
              pageTitle={"Payment Confirmation"}
              authClick={() => {}}
              userClick={() => {}}
              containerStyleProps={{
                style: {
                  position: "sticky",
                  zIndex: 5,
                  top: 0,
                  backgroundColor: "white",
                  width: "98.9%",
                  margin: "0 auto",
                },
              }}
              headerStyleProps={{
                className: "p-4",
              }}
            />
            <div className="p-4">
              <CheckoutPaymentMethodN
                key={C.PAY_NOW}
                paymentOptions={
                  selectedStore?.paymentOptions ||
                  selectedStore?.pickupPaymentOptions ||
                  selectedStore?.dineInPaymentOptions
                }
                handleSuceessOrderRoute={() => {}}
                setcardProcessing={() => {}}
                isPaymentConfirmationPage={true}
              />
            </div>
          </>
        )}
      </div>
      <CommonUI.CustomModal
        open={errorModal?.open}
        reduxAction={handleError}
        bodyclasses="flex"
        overlayClasses="fixed h-screen w-screen top-0 bg-opacity-50 overflow-hidden transition ease-linear duration-700 z-40"
      >
        <CommonUI.ErrorModal
          reduxAction={handleError}
          message={errorModal?.error}
        />
      </CommonUI.CustomModal>
    </>
  );
};

export default AfsPaymentConfirmation;
